import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { ClockIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';

const ServiceSelector = ({ services, selectedService, onServiceSelect, loading }) => {
  const { formatPrice } = useCurrency();
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Service</h2>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!services || services.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Service</h2>
        <div className="text-center py-8">
          <p className="text-gray-500">No services available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Select Service</h2>

      <div className="space-y-3">
        {services.map((service) => {
          const isSelected = selectedService && selectedService.id === service.id;

          return (
            <button
              key={service.id}
              onClick={() => onServiceSelect(service)}
              className={`
                w-full text-left p-4 rounded-lg border-2 transition-all
                ${isSelected
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-offset-2'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {service.name}
                    </h3>
                    {isSelected && (
                      <CheckCircleIcon className="w-6 h-6 text-primary-500 ml-2" />
                    )}
                  </div>

                  {service.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {service.description}
                    </p>
                  )}

                  <div className="flex items-center mt-3 space-x-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="w-4 h-4 mr-1" />
                      <span>{service.duration} min</span>
                    </div>

                    <div className="flex items-center text-sm font-medium text-gray-900">
                      <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                      <span>{formatPrice(service.price)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedService && (
        <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
          <h4 className="font-medium text-primary-900 mb-2">Selected Service</h4>
          <div className="flex items-center justify-between text-sm">
            <span className="text-primary-800">{selectedService.name}</span>
            <div className="flex items-center space-x-4">
              <span className="text-primary-700">{selectedService.duration} min</span>
              <span className="font-semibold text-primary-900">{formatPrice(selectedService.price)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSelector;
